// What the product-cost (COGS) screens tell the user after a save (W20b, 2026-10-07).
//
// Since W20 the backend refuses some cost writes and skips others instead of storing a cost under a
// spelling no report can read:
//   PUT  /api/cogs/brands/:id/cogs/:sku   409 { error: 'ambiguous_master', candidates }
//                                         409 { error: 'case_variant_open_rows', canonical_sku, open_spellings, hint }
//   POST /api/cogs/brands/:id/cogs, POST .../bundles          -> cogs_skipped: [{ sku, reason, ... }]
//   POST .../bundles, PATCH .../products/:sku/active          409 { error: '<human alias message>', canonical_sku }
//   POST /api/warehouse-transfers/:id/receive                 -> cost_rows_skipped: [...]
//   POST /api/procurement/goods-receipts(/split)              -> data.cost_rows_skipped / data.receipts[].cost_rows_skipped
// The PUT hint names an owner-only data-fix flag; it is deliberately never shown or sent from the UI.

import { apiErrorMessage } from './apiErrorMessage';

const join = (list) => (Array.isArray(list) ? list.filter(Boolean).join(' / ') : '');

// Error text for a failed cost write. The two W20 PUT 409 bodies carry a code, not a sentence.
export function cogsWriteErrorMessage(err, fallback) {
  const status = err?.response?.status;
  const data = err?.response?.data || {};
  if (status === 409 && data.error === 'ambiguous_master') {
    return `Not saved: this SKU matches more than one product in this brand (${join(data.candidates)}). `
      + 'Merge or deactivate the duplicate product first, then save again.';
  }
  if (status === 409 && data.error === 'case_variant_open_rows') {
    return `Not saved: ${data.canonical_sku} already has a current cost under another spelling at this warehouse `
      + `(${join(data.open_spellings)}). Changing it would leave a cost that reports cannot read. `
      + 'Ask an admin to clean up the duplicate spelling, then save again.';
  }
  return apiErrorMessage(err, fallback); // DT-1855: also a refusal's { message } (brand gate 403)
}

// Every skipped cost row in one or more save responses (POST/bundle, transfer receive, goods receipts).
export function skippedCostRows(responses) {
  const list = Array.isArray(responses) ? responses : [responses];
  const out = [];
  for (const r of list) {
    if (!r || typeof r !== 'object') continue;
    for (const src of [r, r.data]) {
      if (!src || typeof src !== 'object') continue;
      if (Array.isArray(src.cogs_skipped)) out.push(...src.cogs_skipped);
      if (Array.isArray(src.cost_rows_skipped)) out.push(...src.cost_rows_skipped);
      if (Array.isArray(src.receipts)) {
        for (const gr of src.receipts) {
          if (gr && Array.isArray(gr.cost_rows_skipped)) out.push(...gr.cost_rows_skipped);
        }
      }
    }
  }
  return out;
}

function reasonText(x) {
  switch (x.reason) {
    case 'case_variant_open_rows':
      return `open cost rows under another spelling: ${join(x.spellings)}`;
    case 'ambiguous_master':
      return `more than one product spelling: ${join(x.candidates)}`;
    case 'cost_guard_failed':
      return 'spelling check failed, try again later';
    default:
      return x.reason || 'not recorded';
  }
}

// Bulk imports (COGS CSV, bundles): add result.warning for BulkImportDialog when cost rows were skipped.
export function withCostSkipWarning(result, prefix = 'Import finished') {
  const warning = skippedCostRowsWarning(skippedCostRows(result), prefix);
  return warning ? { ...result, warning } : result;
}

// Non-blocking warning for skipped cost rows, or null when there are none.
export function skippedCostRowsWarning(skipped, prefix = 'Saved') {
  if (!Array.isArray(skipped) || skipped.length === 0) return null;
  const n = skipped.length;
  const items = skipped.map((x) => `${x.sku} (${reasonText(x)})`).join('; ');
  return `${prefix}, but the cost was not updated for ${n} SKU${n === 1 ? '' : 's'}: ${items}. `
    + 'The current cost stays in use.';
}
